import { useEffect, useMemo, useState } from "preact/hooks";
import type { Candidate } from "../engine";
import type { AnswerItem, AttributeCandidate, AttributeItem, ChangeOutcome, EntryItem, GuideChange, GuideContext, GuideItem, SkillCandidate, SkillItem } from "../guide";
import { apexCandidates, apparitionCandidates, boostCandidates, familiarAbilityCandidates, featCandidates, foundationCandidates, guideLevels, keyAbilityCandidates, skillIncreaseCandidates, skillPotencyCandidates, skillTrainingCandidates, slotBoostCandidates, ATTRIBUTE_LABELS } from "../guide";
import type { EntryCandidate } from "../guide";
import { EMPTY_FILTER, browseEntries, traitFacets, type BrowseAdapter, type EntryFilter } from "../data/browse";
import { EntryFilterBar } from "./library";
import { useSheetActions } from "./sheet-context";
import { useSearch } from "./use-data";

export interface PendingChange {
  label: string;
  outcome: ChangeOutcome;
}

interface Props {
  ctx: GuideContext;
  busy: boolean;
  /** Why changes are off (a Session is open), or null. */
  disabled: string | null;
  /** The global Legacy browsing toggle. */
  legacy: boolean;
  onLegacy: (on: boolean) => void;
  onChange: (change: GuideChange, label: string) => Promise<void>;
  onAdvance: () => Promise<void>;
  onBack: () => Promise<void>;
  pending: PendingChange | null;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
  /** A guide item id to open and scroll to (the migration report's link, #17); `onFocused` once done. */
  focus?: string | null;
  onFocused?: () => void;
}

/**
 * The guided level-up (issue #9): every Level up to the Character's, each with its Choices and
 * Answers in the documented order; a picker per item offering only structurally legal
 * candidates with textual prerequisites marked met, unmet or unknown; a per-Choice "show Legacy"
 * affordance; Retraining through the same rows with a preview of what reopens. The iPad layout
 * is #14; this is the functional form.
 */
export function Builder({ ctx, busy, disabled, legacy, onLegacy, onChange, onAdvance, onBack, pending, onConfirm, onCancel, focus = null, onFocused }: Props) {
  const [editing, setEditing] = useState<string | null>(null);
  const [showLegacy, setShowLegacy] = useState<Set<string>>(new Set());
  const levels = useMemo(() => guideLevels(ctx), [ctx]);
  useEffect(() => {
    if (!focus) return;
    setEditing(focus);
    document.getElementById(`guide-item:${focus}`)?.scrollIntoView({ block: "center" });
    onFocused?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);
  const open = levels.reduce((n, l) => n + l.open, 0);
  const level = ctx.character.level;
  const change = async (c: GuideChange, label: string) => {
    setEditing(null);
    await onChange(c, label);
  };
  const toggleLegacy = (id: string, on: boolean) => {
    const next = new Set(showLegacy);
    if (on) next.add(id);
    else next.delete(id);
    setShowLegacy(next);
  };

  return (
    <section class="builder">
      <div class="row">
        <h3>
          Builder {open > 0 ? <span class="tag open">{open} open</span> : <span class="tag">complete</span>}
        </h3>
        <span class="actions">
          <label class="toggle">
            <input type="checkbox" checked={legacy} onChange={(e) => onLegacy((e.target as HTMLInputElement).checked)} /> Show Legacy everywhere
          </label>
          {level > 1 && <button type="button" disabled={busy || !!disabled} onClick={() => void onBack()}>Back to Level {level - 1}</button>}
          <button type="button" class="primary" disabled={busy || !!disabled || level >= 20} onClick={() => void onAdvance()}>
            Advance to Level {Math.min(20, level + 1)}
          </button>
        </span>
      </div>
      {disabled && <p class="notice">{disabled}</p>}
      {open > 0 && <p class="muted">Open Choices never block advancing; they are counted in the badge until filled.</p>}
      {pending && (
        <div class="card pending">
          <p>
            <strong>{pending.label}</strong> reopens {pending.outcome.reopened.length === 1 ? "one Choice" : `${pending.outcome.reopened.length} Choices`} that depended on it:
          </p>
          <ul class="plain">
            {pending.outcome.reopened.map((r) => (
              <li key={`${r.key}#${r.flag ?? ""}`}>
                Level {r.level}: {r.label} <span class="muted">({r.reason})</span>
              </li>
            ))}
          </ul>
          <span class="actions">
            <button type="button" class="primary" disabled={busy} onClick={() => void onConfirm()}>Apply and reopen</button>
            <button type="button" disabled={busy} onClick={onCancel}>Cancel</button>
          </span>
        </div>
      )}
      {levels.map((gl) => (
        <div class="card level" key={gl.level}>
          <div class="row">
            <h4>Level {gl.level}</h4>
            <span class="muted">{gl.open === 0 ? "complete" : gl.open === 1 ? "1 open" : `${gl.open} open`}{gl.level < level ? " · Retraining" : ""}</span>
          </div>
          <ul class="steps">
            {gl.items.map((item) => (
              <li key={item.id} id={`guide-item:${item.id}`} class={item.open ? "open" : ""}>
                <div class="item-row">
                  <span class="item-label">
                    {item.label}
                    {item.kind === "answer" ? <span class="muted"> · {item.entryName}</span> : null}
                  </span>
                  <span class="item-value">
                    <ItemValue item={item} />
                    {item.open && <span class="tag open">open</span>}
                    {item.warning && <span class="tag warn" title={item.warning}>{item.warning}</span>}
                  </span>
                  <span class="actions">
                    {item.kind === "flag" ? (
                      <label class="toggle">
                        <input type="checkbox" checked={item.value} disabled={busy || !!disabled} onChange={(e) => void change({ kind: "answer", key: item.choiceKey, flag: item.flag, value: (e.target as HTMLInputElement).checked ? true : null }, item.label)} />
                        {item.value ? "on" : "off"}
                      </label>
                    ) : (
                      <button type="button" disabled={busy || !!disabled} onClick={() => setEditing(editing === item.id ? null : item.id)}>
                        {editing === item.id ? "Close" : filled(item) ? "Change" : "Choose"}
                      </button>
                    )}
                  </span>
                </div>
                {editing === item.id && (
                  <div class="picker">
                    <Picker ctx={ctx} item={item} busy={busy} legacy={legacy || showLegacy.has(item.id)} perChoiceLegacy={showLegacy.has(item.id)} onLegacy={(on) => toggleLegacy(item.id, on)} onChange={change} />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

export function filled(item: GuideItem): boolean {
  switch (item.kind) {
    case "entry": return item.selection !== null;
    case "answer": return item.prompt.selection !== null;
    case "attribute": return item.value !== null;
    case "skill": return item.value !== null;
    case "flag": return item.value;
  }
}

export function ItemValue({ item }: { item: GuideItem }) {
  const { openEntry } = useSheetActions();
  switch (item.kind) {
    case "entry":
      return item.selection ? (
        <span>
          <button type="button" class="linkish" onClick={() => openEntry(item.selection!.uuid)}>{item.selection.name}</button>
          {item.selection.remaster === false && <span class="tag legacy">Legacy</span>}
        </span>
      ) : <span class="muted">—</span>;
    case "answer":
      return item.prompt.selection !== null ? <span>{item.prompt.selectionLabel ?? String(item.prompt.selection)}</span> : <span class="muted">{item.optional ? "none" : "—"}</span>;
    case "attribute":
      return item.value ? (
        <span>
          {ATTRIBUTE_LABELS[item.value]}
          {item.partial && <span class="tag" title="The modifier was +4 or more already: two partial boosts make +1">partial</span>}
        </span>
      ) : <span class="muted">—</span>;
    case "skill":
      return item.value ? <span>{skillLabel(item.value)}</span> : <span class="muted">—</span>;
    case "flag":
      return null;
  }
}

function skillLabel(slug: string): string {
  return slug.replace(/(^|-)([a-z])/g, (_, sep: string, c: string) => `${sep ? " " : ""}${c.toUpperCase()}`);
}

export interface PickerProps {
  ctx: GuideContext;
  item: GuideItem;
  busy: boolean;
  legacy: boolean;
  perChoiceLegacy: boolean;
  onLegacy: (on: boolean) => void;
  onChange: (change: GuideChange, label: string) => Promise<void>;
}

export function Picker(props: PickerProps) {
  const { item } = props;
  switch (item.kind) {
    case "entry": return <EntryPicker {...props} item={item} />;
    case "answer": return <AnswerPicker {...props} item={item} />;
    case "attribute": return <AttributePicker {...props} item={item} />;
    case "skill": return <SkillPicker {...props} item={item} />;
    case "flag": return null;
  }
}

const PAGE = 80;

const candidateAdapter: BrowseAdapter<EntryCandidate> = { uuid: (c) => c.uuid, name: (c) => c.name, level: (c) => c.level, traits: (c) => c.traits, rarity: (c) => c.rarity };

/** The Library's browser as the picker (#15): the Choice's legal candidates, with the browser's search and filters on top. */
function EntryPicker({ ctx, item, busy, legacy, perChoiceLegacy, onLegacy, onChange }: PickerProps & { item: EntryItem }) {
  const [filter, setFilter] = useState<EntryFilter>({ ...EMPTY_FILTER });
  const search = useSearch();
  const list = useMemo(() => {
    const kind = item.source.kind;
    if (kind === "ancestry" || kind === "heritage" || kind === "background" || kind === "class" || kind === "dual-class") return foundationCandidates(ctx, kind, { legacy });
    if (item.slot === "familiar-ability") return familiarAbilityCandidates(ctx, item.choiceKey, { legacy });
    if (item.slot === "apparition") return apparitionCandidates(ctx, item.choiceKey, { legacy });
    return featCandidates(ctx, item.choiceKey, { legacy });
  }, [ctx, item, legacy]);
  const traits = useMemo(() => traitFacets(list.candidates, candidateAdapter), [list]);
  const shown = useMemo(() => browseEntries(list.candidates, filter, search, candidateAdapter), [list, filter, search]);
  const page = shown.slice(0, PAGE);
  const granted = item.source.kind === "rule-element" || item.source.kind === "feature";
  return (
    <div>
      <EntryFilterBar filter={filter} onChange={setFilter} traits={traits} searchPlaceholder={`Search ${list.candidates.length} candidates`}>
        {(list.hiddenLegacy > 0 || perChoiceLegacy) && (
          <label class="toggle">
            <input type="checkbox" checked={perChoiceLegacy} onChange={(e) => onLegacy((e.target as HTMLInputElement).checked)} /> Show Legacy here{list.hiddenLegacy > 0 ? ` (${list.hiddenLegacy} hidden)` : ""}
          </label>
        )}
        {item.selection && !granted && <button type="button" disabled={busy} onClick={() => void onChange({ kind: "clear", key: item.choiceKey }, `Clearing ${item.label}`)}>Clear</button>}
      </EntryFilterBar>
      {list.note && <p class="muted">{list.note}</p>}
      <ul class="candidates">
        {page.map((c) => <EntryRow key={c.uuid} candidate={c} busy={busy} onPick={() => void onChange({ kind: "select", key: item.choiceKey, uuid: c.uuid }, `${item.label}: ${c.name}`)} />)}
      </ul>
      {shown.length > page.length && <p class="muted">{shown.length - page.length} more; refine the search.</p>}
    </div>
  );
}

function EntryRow({ candidate: c, busy, onPick }: { candidate: EntryCandidate; busy: boolean; onPick: () => void }) {
  const { openEntry } = useSheetActions();
  const status = c.status === "none" ? null : c.status;
  return (
    <li class={c.current ? "current" : ""}>
      <button type="button" class="icon info" aria-label={`Read ${c.name}`} onClick={() => openEntry(c.uuid)}>i</button>
      <button type="button" class="pick" disabled={busy} onClick={onPick}>
        <span class="pick-name">
          {c.name}
          {c.level !== null && c.type === "feat" ? <span class="muted"> {c.level}</span> : null}
        </span>
        <span class="pick-tags">
          {c.current && <span class="tag">current</span>}
          {c.category === "master" && <span class="tag">master ability</span>}
          {!c.remaster && <span class="tag legacy">Legacy</span>}
          {c.rarity !== "common" && <span class="tag">{c.rarity}</span>}
          {status && <span class={`tag status-${status}`} title={c.prerequisites.map((p) => `${p.text}: ${p.status} (${p.reason})`).join("\n")}>{status}</span>}
        </span>
      </button>
      {c.prerequisites.length > 0 && (
        <span class="muted prereqs">
          {c.prerequisites.map((p, i) => (
            <span key={i} class={`prereq status-${p.status}`} title={p.reason}>{p.text}</span>
          ))}
        </span>
      )}
    </li>
  );
}

function AnswerPicker({ item, busy, legacy, perChoiceLegacy, onLegacy, onChange }: PickerProps & { item: AnswerItem }) {
  const [text, setText] = useState("");
  const prompt = item.prompt;
  const all = prompt.candidates ?? [];
  const hiddenLegacy = all.filter((c) => c.remaster === false && !sameValue(c.value, prompt.selection)).length;
  const visible = legacy ? all : all.filter((c) => c.remaster !== false || sameValue(c.value, prompt.selection));
  const needle = text.trim().toLowerCase();
  const shown = needle ? visible.filter((c) => c.label.toLowerCase().includes(needle)) : visible;
  const page = shown.slice(0, PAGE);
  const pick = (c: Candidate) => void onChange({ kind: "answer", key: item.choiceKey, flag: item.flag, value: c.value }, `${item.entryName}: ${c.label}`);
  return (
    <div>
      {prompt.available === false && <p class="notice">{prompt.note ?? "The candidates for this prompt are not available yet."}</p>}
      {prompt.legal === false && <p class="notice">{prompt.note ?? "The current answer is not among the candidates."}</p>}
      {all.length > 12 && (
        <div class="inline">
          <input type="search" placeholder={`Search ${visible.length} candidates`} value={text} onInput={(e) => setText((e.target as HTMLInputElement).value)} />
        </div>
      )}
      {(hiddenLegacy > 0 || perChoiceLegacy) && (
        <label class="toggle">
          <input type="checkbox" checked={perChoiceLegacy} onChange={(e) => onLegacy((e.target as HTMLInputElement).checked)} /> Show Legacy here{hiddenLegacy > 0 ? ` (${hiddenLegacy} hidden)` : ""}
        </label>
      )}
      <ul class="candidates">
        {page.map((c) => (
          <li key={JSON.stringify(c.value)} class={sameValue(c.value, prompt.selection) ? "current" : ""}>
            <button type="button" class="pick" disabled={busy} onClick={() => pick(c)}>
              <span class="pick-name">{c.label}</span>
              <span class="pick-tags">
                {sameValue(c.value, prompt.selection) && <span class="tag">current</span>}
                {c.remaster === false && <span class="tag">Legacy</span>}
              </span>
            </button>
          </li>
        ))}
        {prompt.allowNoSelection && (
          <li>
            <button type="button" class="pick" disabled={busy} onClick={() => void onChange({ kind: "answer", key: item.choiceKey, flag: item.flag, value: null }, `${item.entryName}: no answer`)}>
              <span class="pick-name muted">No answer</span>
            </button>
          </li>
        )}
      </ul>
      {shown.length > page.length && <p class="muted">{shown.length - page.length} more; refine the search.</p>}
      {all.length === 0 && prompt.available !== false && <p class="muted">{prompt.note ?? "No legal candidate: the Choice stays open."}</p>}
    </div>
  );
}

function sameValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function AttributePicker({ ctx, item, busy, onChange }: PickerProps & { item: AttributeItem }) {
  const candidates: AttributeCandidate[] = useMemo(() => {
    switch (item.role) {
      case "free": return boostCandidates(ctx, item.choiceKey);
      case "ancestry":
      case "alternate": return slotBoostCandidates(ctx, "ancestry", item.flag);
      case "background": return slotBoostCandidates(ctx, "background", item.flag);
      case "key": return keyAbilityCandidates(ctx);
      case "apex": return apexCandidates(ctx, item.choiceKey);
      case "voluntary-flaw":
      case "voluntary-boost":
        return (["str", "dex", "con", "int", "wis", "cha"] as const).map((attribute) => ({ attribute, label: ATTRIBUTE_LABELS[attribute], legal: true, reason: null, partial: false, current: item.value === attribute }));
    }
  }, [ctx, item]);
  return (
    <div class="attribute-picker">
      {candidates.map((c) => (
        <button type="button" key={c.attribute} class={`pick${c.current ? " current" : ""}`} disabled={busy || !c.legal} title={c.reason ?? (c.partial ? "Partial boost: the modifier is +4 or more already" : "")}
          onClick={() => void onChange({ kind: "answer", key: item.choiceKey, flag: item.flag, value: c.attribute }, `${item.label}: ${c.label}`)}>
          {c.label}
          {c.partial && <span class="tag">partial</span>}
          {!c.legal && <span class="tag warn">{c.reason}</span>}
        </button>
      ))}
      {item.optional && item.value && <button type="button" disabled={busy} onClick={() => void onChange({ kind: "answer", key: item.choiceKey, flag: item.flag, value: null }, `${item.label}: cleared`)}>Clear</button>}
    </div>
  );
}

function SkillPicker({ ctx, item, busy, onChange }: PickerProps & { item: SkillItem }) {
  const [lore, setLore] = useState("");
  const candidates: SkillCandidate[] = useMemo(() => (item.role === "training" ? skillTrainingCandidates(ctx, item.flag) : item.role === "potency" ? skillPotencyCandidates(ctx, item.choiceKey) : skillIncreaseCandidates(ctx, item.choiceKey, item.intelligence)), [ctx, item]);
  const pick = (slug: string, label: string) => void onChange({ kind: "answer", key: item.choiceKey, flag: item.flag, value: slug }, `${item.label}: ${label}`);
  return (
    <div>
      <ul class="candidates">
        {candidates.map((c) => (
          <li key={c.slug} class={c.current ? "current" : ""}>
            <button type="button" class="pick" disabled={busy || !c.legal} title={c.reason ?? ""} onClick={() => pick(item.role === "training" && c.lore ? c.label : c.slug, c.label)}>
              <span class="pick-name">{c.label}</span>
              <span class="pick-tags">
                <span class="muted">{RANKS[c.rank]}</span>
                {c.current && <span class="tag">current</span>}
                {!c.legal && c.reason && <span class="tag warn">{c.reason}</span>}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {item.role === "training" && (
        <div class="inline">
          <input type="text" placeholder="A Lore skill, e.g. Warfare Lore" value={lore} onInput={(e) => setLore((e.target as HTMLInputElement).value)} />
          <button type="button" disabled={busy || !lore.trim()} onClick={() => pick(lore.trim().replace(/\s*lore$/i, "") + " Lore", lore.trim())}>Train this Lore</button>
        </div>
      )}
    </div>
  );
}

const RANKS = ["untrained", "trained", "expert", "master", "legendary"] as const;
